import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const SEV_COLOR = {
  must_not: "#B5462F", required: "#B5462F", strong: "#C28A2E",
  preferred: "#1F6B6B", brand_note: "#6B6358",
};
const LIFECYCLE_COLOR = { active: "#1F6B6B", draft: "#C28A2E", retired: "#6B6358" };

/**
 * Brand Rules — embedded launcher inside the Brand Kit. Lists this brand's
 * rules with deep links into the full rule-authoring pages (create / detail).
 */
export default function RulesTab({ brandId }) {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/brands/${brandId}/rules`)
      .then(({ data }) => setRules(data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load rules"));
  }, [brandId]);

  return (
    <section data-testid="brand-kit-rules-tab">
      <div className="flex justify-between items-baseline gap-3 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl">Brand Rules</h2>
          <p className="text-sm text-[#3D3A35] mt-2 max-w-2xl">
            Plain-language brand guidance the AI enforces at every step. Open a
            rule to edit, test, or change its lifecycle.
          </p>
        </div>
        <Link
          to={`/brands/${brandId}/rules/new`}
          data-testid="brand-kit-rules-new"
          className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em]"
        >
          + New rule
        </Link>
      </div>

      {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}

      {rules == null ? (
        <p className="text-[#6B6358] mt-6">Loading…</p>
      ) : rules.length === 0 ? (
        <p className="text-[#6B6358] text-sm italic mt-6">
          No rules yet. Author the brand&rsquo;s first rule to start guiding the AI.
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-2">
          {rules.map((r) => (
            <Link
              key={r.rule_id}
              to={`/brands/${brandId}/rules/${r.rule_id}`}
              data-testid={`brand-kit-rule-row-${r.rule_id}`}
              className="border border-[#1A1F2C]/10 bg-white rounded-lg px-4 py-3 hover:border-[#1A1F2C]/30 transition-colors flex items-baseline justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">
                  {r.rule_type}
                </div>
                <div className="text-sm text-[#1A1F2C] mt-0.5 truncate">
                  {r.explanation || r.source_text || r.rule_id}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Badge color={SEV_COLOR[r.severity] || "#6B6358"}>{r.severity}</Badge>
                <Badge color={LIFECYCLE_COLOR[r.lifecycle_status] || "#6B6358"}>{r.lifecycle_status}</Badge>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function Badge({ color, children }) {
  return (
    <span
      className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full"
      style={{ backgroundColor: `${color}1a`, color }}
    >
      {children}
    </span>
  );
}
